import {
  ApolloClient,
  InMemoryCache,
  IdGetterObj,
  from,
  GraphQLRequest,
} from '@apollo/client'
import { AppCookies } from '@src/utils/cookies'
import { Cookies } from 'react-cookie'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { pickBy } from 'lodash'
import getConfig from 'next/config'
import {
  logErrorsInDev,
  hasSessionExpired,
  hasRememberMeSessionExpired,
} from '@utils/errorHandler'
import possibleTypesIntrospection from '@src/types/introspection-result'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import createGraphQLOperationName from '@src/utils/graphQLOperationNameMapper'
import { Router, loginRoute, homeRoute } from '@src/routes'
import { getLocalizedUrl } from '@utils/regional'
import { userSession } from '@src/utils/userSession'
import { NextPageContext } from 'next'
import { isServer } from '@src/utils/isServer'
import axios, { AxiosRequestConfig, AxiosResponseHeaders } from 'axios'

interface IdGetterObjData extends IdGetterObj {
  _id?: string
  value?: string
  key?: string
  name?: string
}

export const createApolloClient = (
  cookies: Cookies,
  ctx,
  initialState = {}
) => {
  const cache = new InMemoryCache({
    possibleTypes: possibleTypesIntrospection.possibleTypes,
    typePolicies: {
      Substance: {
        keyFields: (object: IdGetterObjData) => {
          return object._id || false
        },
      },
      Cart: {
        keyFields: (
          object: IdGetterObjData & { cartType?: string; cartIdAlias?: string }
        ) => {
          const cartType = object.cartType || 'SIAL'
          const alias = object.cartIdAlias
          return alias ? `${alias}-${cartType}` : object.id || false
        },
      },
      ProductComponent: {
        keyFields: (object: IdGetterObjData) => {
          return object.value || false
        },
      },
      Material: {
        keyFields: (object: IdGetterObjData & { name?: string }) => {
          return object.name
            ? `${object.id}-${object.name}`
            : object.id || false
        },
      },
      ProductNumberSuggestions: {
        keyFields: (object: IdGetterObjData) => {
          return object.id ? `${object.id}-barcode` : false
        },
      },
      SharedListMember: {
        // Use default behavior - Apollo will use 'id' or '_id' field
        keyFields: (object: IdGetterObjData) => {
          return object.id ? `${object.id}-SharedListMember` : false
        },
      },
      Product: {
        keyFields: (object: IdGetterObjData & { gaProductCode?: string }) => {
          return object.gaProductCode
            ? `${object.id}-${object.gaProductCode}`
            : object.id || false
        },
      },
      UserActivityEntry: {
        keyFields: (object: IdGetterObjData & { featureType?: string }) => {
          return object.featureType || false
        },
      },
    },
  }).restore(initialState)

  const link = from([
    createHeadersLink(cookies),
    createErrorLink(cookies, cache, ctx),
    createHttpLink(),
  ])

  return new ApolloClient({
    ssrMode: Boolean(ctx),
    link,
    cache,
  })
}

function createHttpLink() {
  const {
    publicRuntimeConfig: { graphQlApiClientUrl, graphQlApiServerUrl },
  } = getConfig()

  let uri
  let fetchOptions
  if (!isServer()) {
    uri = graphQlApiClientUrl
  } else {
    uri = graphQlApiServerUrl
    // We need to dynamically require 'url' and 'https' here since it can only be
    // included with the server build
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const url: typeof import('url') = require('url')

    if (url.parse(uri).protocol === 'https:') {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const https: typeof import('https') = require('https')
      fetchOptions = {
        agent: new https.Agent({
          rejectUnauthorized: false,
        }),
      }
    }
  }

  const customFetch = async (uri, options): Promise<Response> => {
    const axiosConfiguration: AxiosRequestConfig = {
      url: uri,
      method: options?.method,
      headers: options?.headers,
      data: options?.body,
    }
    try {
      if (typeof options.body === 'string') {
        const { operationName } = JSON.parse(options.body)
        if (!operationName) {
          console.error('Error retrieving operation name from options body')
          throw new Error(
            'String body provided to Apollo Client but operation name was not present!'
          )
        }
        axiosConfiguration.url = `${uri}?operation=${operationName}`
      }
      const axiosResponse = await axios(axiosConfiguration)
      return new Response(JSON.stringify(axiosResponse.data), {
        status: axiosResponse.status,
        statusText: axiosResponse.statusText,
        headers: axiosResponse.headers as AxiosResponseHeaders,
      })
    } catch (error: any) {
      const responseBody = {
        message: axios.isAxiosError(error) ? 'Axios error' : 'Unexpected error',
        details: error.message,
      }
      return new Response(JSON.stringify(responseBody), {
        status: error.response?.status || 500,
        statusText: 'Error',
        headers: { 'Content-Type': 'application/json' },
      })
    }
  }

  return createUploadLink({
    uri,
    fetchOptions,
    fetch: customFetch,
  })
}

function createErrorLink(
  cookies: Cookies,
  cache: InMemoryCache,
  ctx: NextPageContext | undefined
) {
  const {
    publicRuntimeConfig: { featureFlags },
  } = getConfig()
  return onError((error) => {
    logErrorsInDev(error)
    if (hasSessionExpired(error) && cookies.get(AppCookies.AccessToken)) {
      // `path` is required below to prevent issues with dynamic routes
      cookies.remove(AppCookies.AccessToken, { path: '/' })
      if (
        featureFlags.profileTokenAuthEnabled &&
        cookies.get(AppCookies.ProfileToken)
      ) {
        cookies.remove(AppCookies.ProfileToken, { path: '/' })
      }
      //remove store since there could be b2b store for b2b users
      cookies.remove(AppCookies.Store, { path: '/' })
      cache.reset()
      if (!isServer()) {
        // Force a window reload, let routes determine if they are protected
        window.location.reload()
      }
    }
    if (
      hasRememberMeSessionExpired(error) &&
      cookies.get(AppCookies.AccessToken)
    ) {
      const currentAsPath = ctx?.asPath || Router.asPath
      const { as, href } = getLocalizedUrl(
        userSession(cookies.getAll()),
        `${loginRoute.index()}?redirect=${encodeURIComponent(
          currentAsPath || homeRoute.index()
        )}&sessionExpired=true`
      )
      if (!isServer()) {
        Router.push(href, as)
      } else if (ctx?.res) {
        if (!ctx.res.headersSent) {
          ctx.res.writeHead(302, {
            Location: as,
          })
        }
        ctx.res.end()
      }
    }
  })
}

// Define cacheable queries dictionary
const cacheableQueriesMap = {
  AemHeaderFooter: true,
  ProductDetail: true,
  SdsCertificateSearch: true,
  EmproveProductDocs: true,
}

function createHeadersLink(cookies) {
  const {
    publicRuntimeConfig: { brandIdentity },
  } = getConfig()

  return setContext((graphQLRequest: GraphQLRequest, { headers = {} }) => {
    // We use `pickBy` here to filter out falsy values since the apollo client
    // will coerce things like `undefined` to a string value ("undefined") otherwise.
    const {
      country,
      accessToken,
      _ga,
      store,
      language,
      isMarketplaceCatalogEnabled,
      isBlueErpIntegrationEnabled,
      userErpType,
      hasOnlyBlueERP,
      isDarmstadtUser,
      profileToken,
    } = cookies.getAll ? cookies.getAll() : cookies
    const authHeaders = pickBy(
      {
        'x-gql-country': country,
        'x-gql-access-token': accessToken,
        'x-gql-guid': _ga,
        'x-gql-store': store,
        'x-gql-language': language,
        'x-gql-mp-enabled': isMarketplaceCatalogEnabled,
        'x-gql-blue-erp-enabled': isBlueErpIntegrationEnabled,
        'x-gql-user-erp-type': userErpType,
        'x-gql-has-only-blue-erp': hasOnlyBlueERP,
        'x-gql-is-darmstadt-user': isDarmstadtUser,
        'x-gql-requesting-website': brandIdentity,
        'x-gql-profile-token': profileToken,
      },
      Boolean
    )
    const graphQLOperationHeader = createGraphQLOperationName(graphQLRequest)

    return {
      headers: {
        ...headers,
        ...authHeaders,
        ...graphQLOperationHeader,
        // If there's an operation name, and that operation is in the cacheable queries dictionary, set the cacheable header, else set blank object
        ...(graphQLRequest['operationName']
          ? cacheableQueriesMap[graphQLRequest.operationName]
            ? { 'x-gql-cacheable': 'true' }
            : {}
          : {}),
      },
    }
  })
}
